import { Badge } from "react-bootstrap";
import ExplainLink from "./ExplainLink";
import { useHouseholdResolver } from "./hooks/useAccountResolver";
import { useAudit } from "./hooks/useAudit";
import type { AuditFinding, FindingSeverity } from "./lib/explainTypes";

interface Props {
  rebalanceId: number;
  generatedAt: string;
  householdId: number;
}

// Severity → bootstrap badge variant, mirroring ChecksPanel.
const SEVERITY_VARIANT: Record<FindingSeverity, string> = {
  critical: "danger",
  error: "danger",
  warning: "warning",
  info: "info",
};

// Descending severity precedence so the rollup badge reflects the worst.
const SEVERITY_RANK: Record<FindingSeverity, number> = {
  critical: 3,
  error: 2,
  warning: 1,
  info: 0,
};

/**
 * Severity rollup for the audit findings primed onto the tree (D8). Reads the
 * same `useAudit` cache stream the inline cards read (no extra request), and
 * renders the pointer's `maxSeverity` (falling back to the worst finding
 * severity) plus the total finding count. Renders nothing when there are no
 * audit findings so a non-audited rebalance's header is unchanged.
 */
function AuditSeverityRollup({ rebalanceId }: { rebalanceId: number }) {
  const { data } = useAudit(rebalanceId > 0 ? rebalanceId : undefined);
  const audit = data?.data;
  const findings: AuditFinding[] = audit?.findings ?? [];
  if (findings.length === 0) return null;

  const worst =
    audit?.maxSeverity ??
    findings.reduce<FindingSeverity>(
      (acc, f) =>
        SEVERITY_RANK[f.severity] > SEVERITY_RANK[acc] ? f.severity : acc,
      "info",
    );

  return (
    <Badge
      bg={SEVERITY_VARIANT[worst]}
      className="ms-2 align-middle"
      aria-label={`Audit ${worst}`}
    >
      Audit: {worst.toUpperCase()} ({findings.length})
    </Badge>
  );
}

export default function ExplainHeader({
  rebalanceId,
  generatedAt,
  householdId,
}: Props) {
  const household = useHouseholdResolver();
  // The synchronous manual rebalance is unpersisted, so it has no rebalance id
  // (`0`). Render it as "Manual Rebalance" rather than the misleading
  // "Rebalance #0". Persisted rebalances (id > 0) are unchanged. (APP-1248)
  const title =
    rebalanceId > 0 ? `Rebalance #${rebalanceId}` : "Manual Rebalance";
  return (
    <div className="mb-3">
      <h2>
        {title} —{" "}
        <ExplainLink to={`/clients/households/${householdId}/overview`}>
          {household.name ?? `Household ${householdId}`}
        </ExplainLink>
        <AuditSeverityRollup rebalanceId={rebalanceId} />
      </h2>
      <div className="text-muted">
        Generated {new Date(generatedAt).toLocaleString()}
      </div>
    </div>
  );
}
